import type { GeoCoords } from '@/core/data/location/location.interface';
import { haversineDistance } from '@/core/helpers/location.helper';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { computed, type Ref } from 'vue';

export enum SpotProximityStatus {
  NEARBY = 'NEARBY',
  FAR = 'FAR',
  NO_GPS = 'NO_GPS',
}

const NEARBY_THRESHOLD_METERS = 50;

export function useSpotProximity(spotCoordinates: Ref<GeoCoords | undefined | null>) {
  const geolocationStore = useGeolocationStore();

  const distance = computed((): number | null => {
    const coords = spotCoordinates.value;
    const userLoc = geolocationStore.userLocation;

    if (!coords || !userLoc?.location) return null;

    return haversineDistance(coords, userLoc.location);
  });

  const gpsAccuracy = computed((): number | null => {
    return geolocationStore.userLocation?.accuracy ?? null;
  });

  const status = computed((): SpotProximityStatus => {
    if (distance.value === null) return SpotProximityStatus.NO_GPS;
    if (distance.value <= NEARBY_THRESHOLD_METERS) return SpotProximityStatus.NEARBY;
    return SpotProximityStatus.FAR;
  });

  const isNearby = computed(() => status.value === SpotProximityStatus.NEARBY);
  const isFar = computed(() => status.value === SpotProximityStatus.FAR);
  const hasGps = computed(() => status.value !== SpotProximityStatus.NO_GPS);
  const canInteract = computed(() => isNearby.value);

  return {
    status,
    distance,
    gpsAccuracy,
    isNearby,
    isFar,
    hasGps,
    canInteract,
  };
}
